/**
 * Замер того, что реально едет в трансляции: разрешение, кадры, битрейт и причина ограничения.
 * Нужен, чтобы на вопрос «почему 30 кадров» отвечали цифры, а не догадки: браузер честно говорит,
 * упёрся ли кодировщик в процессор или в канал (qualityLimitationReason).
 */

export type QualityLimit = 'none' | 'cpu' | 'bandwidth' | 'other'

export interface VideoStats {
  width: number
  height: number
  fps: number
  kbps: number
  codec?: string | undefined
  /** Имя реализации, если браузер его раскрывает. Наличие powerEfficient не доказывает GPU. */
  encoder?: string | undefined
  encodeMs?: number | undefined
  /**
   * Сколько кадров отдаёт сам захват (media-source). Только для своей трансляции.
   * Без этого числа «почему 30, а выбрано 60» неразрешимо: кадров может не давать
   * система, а может не успевать кодировщик — лечится это по-разному.
   */
  sourceFps?: number | undefined
  /** Только для своей трансляции: во что упёрся кодировщик. */
  limit?: QualityLimit | undefined
}

/** Счётчики прошлого замера: битрейт считаем по приросту байтов. */
export interface StatsCursor {
  bytes: number
  at: number
  streams?: Record<string, StreamCursor> | undefined
}

interface StreamCursor {
  bytes: number
  at: number
  frames?: number | undefined
  encodeTime?: number | undefined
}

interface RtpLike {
  type?: string
  kind?: string
  id?: string
  frameWidth?: number
  frameHeight?: number
  framesPerSecond?: number
  bytesSent?: number
  bytesReceived?: number
  timestamp?: number
  qualityLimitationReason?: string
  codecId?: string
  mediaSourceId?: string
  active?: boolean
  framesEncoded?: number
  totalEncodeTime?: number
  encoderImplementation?: string
}

const LIMITS: QualityLimit[] = ['none', 'cpu', 'bandwidth', 'other']

/** Собираем также отправители резервного кодека: основной может уже не передавать кадры. */
export async function getVideoStatsReport(track: {
  getRTCStatsReport: () => Promise<RTCStatsReport | undefined>
  simulcastCodecs?: ReadonlyMap<string, { sender?: Pick<RTCRtpSender, 'getStats'> | undefined }> | undefined
}): Promise<RTCStatsReport | undefined> {
  const requests = [track.getRTCStatsReport().catch(() => undefined)]
  for (const stream of track.simulcastCodecs?.values() ?? []) {
    if (stream.sender) requests.push(stream.sender.getStats().catch(() => undefined))
  }
  const reports = await Promise.all(requests)
  const merged = new Map<string, RTCStats>()
  for (const report of reports) report?.forEach((stat, id) => merged.set(id, stat))
  return merged.size ? merged : undefined
}

/**
 * Разбирает отчёт getStats(): берёт видеопоток (исходящий или входящий) и считает битрейт
 * по приросту байтов с прошлого замера. Возвращает null, пока считать не из чего.
 */
export function readVideoStats(report: RTCStatsReport, prev: StatsCursor | null): { stats: VideoStats; cursor: StatsCursor } | null {
  const streams: Record<string, StreamCursor> = {}
  const candidates: { rtp: RtpLike; key: string; producing: boolean; rate: number }[] = []
  const sources: RtpLike[] = []
  report.forEach((raw) => {
    const rtp = raw as RtpLike
    if (rtp.type === 'media-source' && rtp.kind === 'video') sources.push(rtp)
    if (rtp.kind !== 'video' || (rtp.type !== 'outbound-rtp' && rtp.type !== 'inbound-rtp')) return
    const codec = rtp.codecId ? report.get(rtp.codecId) as { mimeType?: string } | undefined : undefined
    if (/^video\/(rtx|red|ulpfec|flexfec)/i.test(codec?.mimeType ?? '')) return
    const key = `${rtp.id ?? ''}|${rtp.codecId ?? ''}`
    const now = { bytes: rtp.bytesSent ?? rtp.bytesReceived ?? 0, at: rtp.timestamp ?? 0, frames: rtp.framesEncoded, encodeTime: rtp.totalEncodeTime }
    streams[key] = now
    const before = prev?.streams?.[key]
    const seconds = before && now.at > before.at ? (now.at - before.at) / 1000 : 0
    const delta = before ? now.bytes - before.bytes : 0
    candidates.push({
      rtp, key,
      producing: rtp.active !== false && ((rtp.framesPerSecond ?? 0) > 0 || (now.frames ?? 0) > (before?.frames ?? now.frames ?? 0)),
      rate: seconds > 0 ? Math.max(0, delta) / seconds : now.bytes,
    })
  })
  candidates.sort((a, b) => Number(b.rtp.active !== false) - Number(a.rtp.active !== false) || Number(b.producing) - Number(a.producing) || b.rate - a.rate)
  const best = candidates[0]
  if (!best) return null
  const { rtp, key } = best
  const now = streams[key]!
  const before = prev?.streams?.[key]
  const seconds = before && now.at > before.at ? (now.at - before.at) / 1000 : 0
  const kbps = seconds > 0 && now.bytes >= before!.bytes ? Math.round(((now.bytes - before!.bytes) * 8) / seconds / 1000) : 0
  const encoded = before?.frames !== undefined && now.frames !== undefined ? now.frames - before.frames : 0
  const encodeTime = before?.encodeTime !== undefined && now.encodeTime !== undefined ? now.encodeTime - before.encodeTime : -1
  const encodeMs = rtp.type === 'outbound-rtp' && encoded > 0 && encodeTime >= 0 ? Math.round(encodeTime * 1000 / encoded * 10) / 10 : undefined
  const codec = rtp.codecId ? report.get(rtp.codecId) as { mimeType?: string } | undefined : undefined
  const source = (rtp.mediaSourceId ? report.get(rtp.mediaSourceId) as RtpLike | undefined : undefined) ?? (sources.length === 1 ? sources[0] : undefined)
  const sourceFps = source?.framesPerSecond !== undefined ? Math.round(source.framesPerSecond) : undefined
  return {
    stats: {
      width: rtp.frameWidth ?? 0,
      height: rtp.frameHeight ?? 0,
      fps: rtp.active === false ? 0 : Math.round(rtp.framesPerSecond ?? 0),
      kbps: Math.max(0, kbps),
      codec: codec?.mimeType?.replace(/^video\//i, ''),
      encoder: rtp.type === 'outbound-rtp' ? rtp.encoderImplementation : undefined,
      encodeMs: encodeMs !== undefined && Number.isFinite(encodeMs) ? encodeMs : undefined,
      sourceFps: rtp.type === 'outbound-rtp' ? sourceFps : undefined,
      limit: rtp.type === 'outbound-rtp' ? LIMITS.find((l) => l === rtp.qualityLimitationReason) ?? 'none' : undefined,
    },
    cursor: { ...now, streams },
  }
}

export function formatBitrate(kbps: number): string {
  return kbps >= 1000 ? `${(kbps / 1000).toFixed(1).replace('.', ',')} Мбит/с` : `${kbps} кбит/с`
}

export const LIMIT_LABELS: Record<QualityLimit, string> = {
  none: '',
  cpu: 'упирается в процессор',
  bandwidth: 'упирается в канал',
  other: 'ограничено браузером',
}
